import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { selectedPageStore } from '../stores/selectedPage.store';
import { PageLayout } from '../layouts/PageLayout';
import _ from 'lodash';
import 'dayjs/locale/ko';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import {
  IApiResult,
  IOptionItem,
  ILeftMenu,
  ILoginUser,
  IPageContent,
  IPopup,
  IApiListResult,
} from '../interfaces/app.interface';
import { loginStore } from '../stores/login.store';
import {
  refreshListStore,
  removeRefreshListStore,
} from '../stores/refreshList.store';
import { pageContentStore } from '../stores/page.store';
import * as cmnApi from '../apis/cmn.api';
import * as sttnApi from '../apis/sttn.api';
import * as nstlApi from '../apis/nstl.api';
import { PbFormBox } from '../components/PbFormBox';
import {
  ActionSheet,
  Button,
  Cascader,
  CascaderView,
  Ellipsis,
  InfiniteScroll,
  Input,
  List,
} from 'antd-mobile';
import {
  Action,
  ActionSheetShowHandler,
} from 'antd-mobile/es/components/action-sheet';
import { PbFormBoxItem } from '../components/PbFormBoxItem';
import { nanoid } from 'nanoid';
import { addPopupStore } from '../stores/popup.store';
import { StockRequestHistoryDetail } from '../popups/StockRequestHistoryDetail';
import CalendarComponent from '../popups/StockRequestHistoryDetail/Calendar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * 현황 관리 > 재고 이동 요청 이력
 * @constructor
 */

// 목록 데이터의 현재 페이지 번호를 정의함
let valCurrentPage: number = 0;

// 선택한 재고 이동 요청 접수 유형을 정의함
let valSttnType: string[] = [];

// 요청 기간을 정의함
let valDayState: [string | undefined | null, string | undefined | null] = [
  null,
  null,
];

const StockRequestHistoryPage = () => {
  // 언어를 정의함
  const { t } = useTranslation();

  // 로그인한 사용자 저장소를 정의함
  const [loginUser, setLoginUser] = useRecoilState<ILoginUser>(loginStore);

  // 선택한 페이지 저장소를 정의함
  const [selectedPage, setSelectedPage] =
    useRecoilState<ILeftMenu>(selectedPageStore);

  // 페이지 내용 저장소를 정의함
  const [pageContent, setPageContent] =
    useRecoilState<IPageContent>(pageContentStore);

  // 목록 새로고침 저장소를 정의함
  const [refreshList, setRefreshList] =
    useRecoilState<string[]>(refreshListStore);

  // 삭제할 목록 새로고침 저장소를 정의함
  const [removeRefreshList, setRemoveRefreshList] = useRecoilState<string>(
    removeRefreshListStore,
  );

  // 추가할 팝업 저장소를 정의함
  const [addPopup, setAddPopup] = useRecoilState<IPopup | null>(addPopupStore);

  const [options, setOptions] = useState<string | any>([]);

  // 재고 이동 요청 접수 유형 옵션을 정의함
  const [sttnTypeOption, setSttnTypeOption] = useState<any[]>([]);

  // 선택한 재고 이동 요청 접수 유형을 정의함
  const [sttnType, setSttnType] = useState<string[]>([]);

  // 선택한 재고 이동 요청 접수 유형 문자열을 정의함
  const [sttnTypeText, setSttnTypeText] = useState<JSX.Element[]>([]);

  /**
   * 공통 관련
   */

  // 공통코드를 불러옴
  const getCommonCode = (
    code: string,
    defaultItem: IOptionItem | null = null,
    callback: Function = () => {},
  ) => {
    let tmpOptionItem: Action[] = [];

    // 상세 공통코드를 불러옴
    cmnApi
      .getDetailCodesAll({ grpCodeId: code })
      .then((data: IApiListResult) => {
        // 기본 아이템이 있으면 아이템 목록에 추가함
        if (defaultItem !== null) {
          tmpOptionItem.push({
            text: defaultItem.label,
            key: defaultItem.value,
          });
        }

        // 불러온 데이터를 아이템 목록에 추가함
        _.sortBy(data.data.list, ['sortRdr']).map(
          (item: any, index: number) => {
            tmpOptionItem.push({
              text: item.dtlCodeDscr,
              key: item.dtlCodeId,
            });
          },
        );

        // 콜백 함수를 실행함
        callback(tmpOptionItem);
      });
  };

  // 목록을 초기화하고 목록을 다시 불러옴
  const getList = () => {
    // 목록 데이터를 초기화함
    setListData([]);

    // 목록 데이터의 현재 페이지 번호를 초기화함
    valCurrentPage = 0;

    // 목록 데이터를 추가로 불러옴
    loadMoreListData();
  };

  /**
   * 검색 관련
   */

  // 검색 입력을 정의함
  const [searchInput, setSearchInput] = useState<string>('');

  const [dayState, setDayState] = useState<
    [string | undefined | null, string | undefined | null]
  >([null, null]);
  // 검색 입력의 값을 변경함
  const handleSearchInput_onChange = (event: any) => {
    setSearchInput(event);
  };

  // 검색 버튼을 클릭함
  const handleSearchButton_onClick = () => {
    // 목록 데이터를 초기화함
    setListData([]);

    // 목록 데이터의 현재 페이지 번호를 초기화함
    valCurrentPage = 0;

    // 목록 데이터를 추가로 불러옴
    loadMoreListData();
  };

  /**
   * 목록 관련
   */

  // 목록 데이터를 정의함
  const [listData, setListData] = useState<any[]>([]);

  // 추가로 불러올 목록 데이터의 여부를 정의함
  const [hasMoreListData, setHasMoreListData] = useState<boolean>(true);

  // 목록 데이터를 추가로 불러옴
  const loadMoreListData = async () => {
    // 불러올 페이지 번호를 증가함
    valCurrentPage += 1;

    // 목록을 불러옴
    getListData(valCurrentPage, (data: any[]) => {
      // 불러온 데이터를 목록 데이터에 추가함
      setListData((pre: any) => [...pre, ...data]);

      // 불러온 데이터가 있으면 추가로 불러올 목록 데이터가 존재한다고 적용함
      setHasMoreListData(data.length > 0);
    });
  };

  // 재고 이동 요청 목록을 불러옴
  const getListData = (
    paramCurrentPage: number = 1,
    callback: Function = () => {},
  ) => {
    console.log('> 재고이동', {
      searchKeyword: searchInput.trim(),
      searchType: 'prjc_name',
      pageSize: 30,
      stDate: valDayState[0] ?? '',
      endDate: valDayState[1] ?? '',
      // stckSttsCode: '',
      // stckTypeCode: valSttnType,
      currPageIdx: paramCurrentPage,
      cmpnCode: loginUser.cmpnCode || '',
      userId: loginUser.id,
    });

    sttnApi
      .getSttnNvnt({
        searchKeyword: searchInput.trim(),
        searchType: 'prjc_name',
        pageSize: 30,
        stDate: valDayState[0] ?? '',
        endDate: valDayState[1] ?? '',
        // stckSttsCode: '',
        // stckTypeCode: valSttnType,
        currPageIdx: paramCurrentPage,
        cmpnCode: loginUser.cmpnCode || '',
        userId: loginUser.id,
      })
      .then((data: IApiListResult) => {
        if (data.data.list !== undefined) {
          callback(data.data.list);
        }
      })
      .catch((error: any) => {});
  };

  // 목록의 행을 클릭함
  const handleListData_onClick = (data: any) => {
    let tmpId: string = nanoid();

    // 팝업을 추가함
    setAddPopup({
      id: tmpId,
      title: data.prjcName,
      content: <StockRequestHistoryDetail id={data.docEntry} />,
      widthSizePercent: 100,
      heightSizePercent: 70,
      position: 'bottom',
      maskClick: true,
      applyPadding: false,
    });
  };

  /**
   * 이벤트
   */

  // 페이지 로딩 후 한번만 실행함
  useEffect(() => {
    return () => {};
  }, []);

  // 사용자 정보가 준비됐을 때 실행함
  useEffect(() => {
    if (loginUser.id === '') {
      return;
    }

    // 재고 이동 요청 접수 유형 옵션을 정의함
    let tmpStockOptionItem: any[] = [];

    // 재고 이동 요청 접수 유형 옵션에 기본값을 추가함
    setSttnTypeOption([
      {
        label: '전체',
        value: '',
      },
    ]);

    cmnApi
      .getDetailCodesAll({ grpCodeId: 'STCK_TYPE' })
      .then((data: IApiResult) => {
        data.data.list.map((item: any, index: number) => {
          tmpStockOptionItem.push({
            label: item.dtlCodeDscr,
            value: item.dtlCodeId,
            children: [],
          });

          cmnApi
            .getDetailCodesAll({ grpCodeId: 'STCK_STTS' })
            .then((subData: IApiResult) => {
              // 상세 유형의 부모를 찾음
              let StockOptionParent: any = _.find(tmpStockOptionItem, {
                value: item.dtlCodeId,
              });

              // 상세 유형에 기본값을 추가함
              StockOptionParent.children.push({
                label: '전체',
                value: '',
              });

              if (subData.data.list !== undefined) {
                // 옵션의 자식에 추가함
                subData.data.list.map((subItem: any) => {
                  if (item.dtlCodeId === 'STCK_RTRN') {
                    StockOptionParent.children.push({
                      label: subItem.dtlCodeDscr,
                      value: subItem.dtlCodeId,
                    });
                  }
                });
              }

              setSttnTypeOption((pre: any[]) => [...pre, StockOptionParent]);
            });
        });
      });

    // 목록 데이터를 추가로 불러옴
    loadMoreListData();

    return () => {};
  }, [loginUser.id]);

  // 선택한 재고 이동 요청 접수 유형이 변경됐을 때 실행함
  useEffect(() => {
    if (loginUser.id === '') {
      return;
    }

    let tmpSttnTypeText: JSX.Element[] = [];

    // 선택한 재고 이동 요청 접수 유형을 문자열로 변환함
    if (sttnType.length > 0) {
      // 부모 옵션에서 선택한 재고 이동 요청 접수 유형을 찾음
      let tmpStep1: any = _.find(sttnTypeOption, { value: sttnType[0] });

      // 코드에 해당하는 문자열을 추가함
      tmpSttnTypeText.push(
        <div>
          <span className="text-md text-indigo-500">{tmpStep1.label}</span>
        </div>,
      );

      if (sttnType.length > 1) {
        // 부모 옵션의 자식 옵션에서 선택한 재고 이동 요청 접수 상세 유형을 찾음
        let tmpStep2: any = _.find(tmpStep1.children, { value: sttnType[1] });

        // 코드에 해당하는 문자열을 추가함
        tmpSttnTypeText.push(
          <div>
            <span className="text-md text-indigo-500">{tmpStep2.label}</span>
          </div>,
        );
      }
    }

    // 선택한 재고 이동 요청 접수 유형이 없으면 '전체'로 적용함
    if (tmpSttnTypeText.length === 0) {
      tmpSttnTypeText.push(
        <div>
          <span className="text-md text-indigo-500">전체</span>
        </div>,
      );
    }

    // 선택한 재고 이동 요청 접수 유형 문자열에 적용함
    setSttnTypeText(tmpSttnTypeText);

    return () => {};
  }, [loginUser.id, sttnType]);

  return (
    <PageLayout pageInfoBarLeftArea="재고 이동 요청 이력">
      {/* 검색 폼 박스 */}
      <PbFormBox label="검색">
        <div className="space-y-3">
          {/* 항목 */}
          <PbFormBoxItem label="재고 이동 요청 유형">
            {/* 버튼 */}
            <Button
              size="middle"
              fill="outline"
              onClick={async () => {
                const result: any = await Cascader.prompt({
                  options: sttnTypeOption,
                  title: '재고 이동 요청 유형',
                  placeholder: '선택하세요',
                  cancelText: '취소',
                  confirmText: '적용',
                });

                // 선택한 재고 이동 요청 유형에 적용함
                setSttnType(result === null ? [''] : result);

                // 재고 이동 요청 유형에 적용함
                valSttnType = result === null ? [''] : result;

                // 목록을 초기화하고 목록을 다시 불러옴
                getList();
              }}
              className="w-full !border-indigo-200"
            >
              <div className="space-y-1">{sttnTypeText}</div>
            </Button>
          </PbFormBoxItem>
          {/* 캘린더 */}
          <PbFormBoxItem label="요청 기간">
            <CalendarComponent dayState={dayState} callback={setDayState} />
          </PbFormBoxItem>
          {/* 항목 */}
          <PbFormBoxItem label="검색어">
            {/* 텍스트 입력 */}
            <Input
              placeholder="프로젝트명을 입력하세요."
              onChange={handleSearchInput_onChange}
              onEnterPress={handleSearchButton_onClick}
              value={searchInput}
              clearable={true}
            />
          </PbFormBoxItem>
          {/* 항목 */}
          <PbFormBoxItem>
            {/* 버튼 */}
            <Button
              size="large"
              shape="default"
              onClick={handleSearchButton_onClick}
              className="w-full !bg-indigo-700"
            >
              <span className="text-md text-white">검색</span>
            </Button>
          </PbFormBoxItem>
        </div>
      </PbFormBox>

      {/* 목록 폼 박스 */}
      <List className="!rounded-lg overflow-hidden">
        {listData.map((item: any, index: number) => (
          <List.Item key={index}>
            <div
              onClick={() => handleListData_onClick(item)}
              className="button-event space-y-1"
            >
              {/* 프로젝트 이름 */}
              <div className="flex justify-start items-center space-x-2">
                <span className="text-base text-gray-700 font-semibold">
                  <Ellipsis direction="end" content={item.prjcName} />
                </span>
              </div>

              {/* 협력사 이름, 일자 */}
              <div className="flex justify-between items-center space-x-2">
                <div className="flex justify-start items-center">
                  <span className="text-base text-gray-500">
                    {/*{item.bpName}*/}
                    <Ellipsis direction="end" content={item.bpName} />
                  </span>
                </div>
                <div className="flex justify-end items-center">
                  <span className="text-xs text-gray-400">{item.rgstDate}</span>
                </div>
              </div>

              {/* 재고 현황 */}
              <div className="w-full flex justify-between items-center">
                <div className="w-full flex justify-center items-center space-x-1">
                  {/* 재고추가 */}
                  <div className="w-1/3 px-2 py-1 bg-gray-100 rounded">
                    <div className="flex justify-center items-center">
                      <span className="text-sm text-gray-600 font-bold">
                        {item.stckAdCnt}
                      </span>
                    </div>

                    <div className="flex justify-center items-center">
                      <span className="text-xs text-gray-600">재고추가</span>
                    </div>
                  </div>

                  {/* 재고반납 일반 */}
                  <div className="w-1/3 px-2 py-1 bg-gray-100 rounded">
                    <div className="flex justify-center items-center">
                      <span className="text-sm text-gray-600 font-bold">
                        {item.stckRtrnCmnCnt}
                      </span>
                    </div>

                    <div className="flex justify-center items-center">
                      <span className="text-xs text-gray-600">
                        재고반납 일반
                      </span>
                    </div>
                  </div>

                  {/* 재고반납 불량 */}
                  <div className="w-1/3 px-2 py-1 bg-gray-100 rounded">
                    <div className="flex justify-center items-center">
                      <span className="text-sm text-gray-600 font-bold">
                        {item.stckRtrnErrCnt}
                      </span>
                    </div>

                    <div className="flex justify-center items-center">
                      <span className="text-xs text-gray-600">
                        재고반납 불량
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </List.Item>
        ))}
      </List>

      {/* 더 불러오기 */}
      <div className="flex justify-center items-center">
        <div
          onClick={loadMoreListData}
          className="translateZ button-event px-10 py-2 space-y-1 animate-pulse"
        >
          <div className="flex justify-center items-center">
            <span className="text-xs text-gray-500">더 불러오기</span>
          </div>

          <div className="flex justify-center items-center">
            <FontAwesomeIcon
              icon={['fas', 'chevron-down']}
              className="w-3 h-3 text-gray-500"
            />
          </div>
        </div>
      </div>

      {/*<InfiniteScroll*/}
      {/*  loadMore={loadMoreListData}*/}
      {/*  hasMore={hasMoreListData}*/}
      {/*  className="!h-1"*/}
      {/*>*/}
      {/*  <span className="text-xs text-gray-400 !h-1">*/}
      {/*    불러올 데이터가 더 이상 없습니다.*/}
      {/*  </span>*/}
      {/*</InfiniteScroll>*/}
    </PageLayout>
  );
};

export default StockRequestHistoryPage;
